/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Channel as IChannel,
    ChannelsApi,
    ChannelsGetRequest,
    Configuration,
    ConfigurationParameters,
    SourceType,
    StationPolicy,
    VideoResolution,
} from 'client-code/channels-service';
import { Channels } from '../entities/Channels';
import { IChannelsServiceRepository } from '../interfaces/IChannelsServiceRepository';
import { Log } from '../logging/Log';
import { TServicesParams } from '../types/TServicesParams';
import { Channel } from '../entities/Channel';
import { Storage } from '../storage/Storage';
import { Constants } from '../constants';
import { TChannels } from '../types/TChannels';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { CommonHeaders } from '../CommonHeaders';
import { CachingPolicy } from '../cachingpolicy/CachingPolicy';
import { ChannelsType } from '../types/TChannelsType';
import { StationPolicyService } from '../infrastructure/StationPolicyService';
import { DeviceModel } from '../device/DeviceModel';
import { DeviceFeatures } from '../device/DeviceFeatures';
import { APPLICATION_FEATURE_AREA } from '../../config';

const SubscribedChannelsType = {
    NEXT: 'NEXT',
    PREVIOUS: 'PREVIOUS',
} as const;

type SubscribedChannelsType = typeof SubscribedChannelsType[keyof typeof SubscribedChannelsType];

export class ChannelsRepositoryImpl implements IChannelsServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'ChannelsRepositoryImpl';

    private static stationPoliciesMap: Map<string, StationPolicy> = new Map();

    private static allChannels: Array<string> = [];

    private static allChannelsIndexMap: { [channelNumber: string]: number } = {};

    private static subscribedChannels: Array<string> = [];

    private static subscribedChannelsIndexMap: { [channelNumber: string]: number } = {};

    constructor(params: TServicesParams) {
        this.mParams = params;
        this.updateArrayIndexMapping();
    }

    /**
     * get channels from server
     * @returns Channels
     */
    private async getChannelsFromServer(isReceived: boolean = false): Promise<Channels> {
        try {
            const snapshotVersion = Storage.getInstance().getItem(
                Storage.Keys.CHANNELS_SNAPSHOT_VERSION,
            );

            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE).headers;

            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };
            const requestParameters: ChannelsGetRequest = {
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                bodyId: this.mParams.bodyId,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                isFavorite: false,
                isReceived,
                snapshotVersion,
            };
            const configuration = new Configuration(configurationParameters);
            const channelsService = new ChannelsApi(configuration);
            const serviceResponse = await channelsService.channelsGet(requestParameters);
            // Filter jump and PPV channels based on flag
            // TODO: Remove custom flags once available from deviceFeatureSearch
            const filteredChannelsResponse = this.getfilteredChannels(
                serviceResponse.data.channels,
            );

            const channelsData = filteredChannelsResponse?.map(
                (channel) => new Channel(channel, this.mParams.channelLogoBaseUrl),
            );

            return new Channels({
                ...serviceResponse.data,
                channels: channelsData,
            });
        } catch (error) {
            Log.e(this.TAG, 'getChannels() - failed to fetch channels data', error);
            throw error;
        }
    }

    private async fetchAndStoreAllChannels(): Promise<void> {
        try {
            const [allChannels, subscribedChannels, stationPolicyData] = await Promise.all([
                this.getChannelsFromServer(),
                this.getChannelsFromServer(true),
                new StationPolicyService(this.mParams).fetchStationPolicies(),
            ]);

            if (!allChannels.channels.length) {
                allChannels.channels = this.getChannelsFromStore();
            }

            allChannels.channels.forEach((channel, _index) => {
                ChannelsRepositoryImpl.allChannelsIndexMap[channel.channelNumber!] = _index;
            });

            if (subscribedChannels.channels.length) {
                subscribedChannels.channels.forEach((channel) => {
                    const channelIndex =
                        ChannelsRepositoryImpl.allChannelsIndexMap[channel.channelNumber!];
                    const unsubscribedChannel = allChannels.channels[channelIndex];
                    unsubscribedChannel.isSubscribed = true;
                });
            }

            Storage.getInstance().setItem(Storage.Keys.CHANNELS, allChannels.channels);
            Storage.getInstance().setItem(
                Storage.Keys.CHANNELS_SNAPSHOT_VERSION,
                allChannels.snapshotVersion,
            );

            if (stationPolicyData) {
                stationPolicyData.forEach((policy: StationPolicy) => {
                    ChannelsRepositoryImpl.stationPoliciesMap.set(policy.name, policy);
                });
            }
        } catch (error) {
            Log.e(this.TAG, 'fetchAndStoreAllChannels() - cannot store channels data', error);
            throw error;
        }
    }

    private async updateChannelsCache(): Promise<void> {
        try {
            const currentTime = Date.now();
            const channelsDataExpiryTime = Storage.getInstance().getItem(
                Storage.Keys.CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME,
            );
            const localCacheExpired: boolean =
                !channelsDataExpiryTime || channelsDataExpiryTime < currentTime;
            if (localCacheExpired) {
                await this.fetchAndStoreAllChannels();
                this.updateArrayIndexMapping();
                Storage.getInstance().setItem(
                    Storage.Keys.CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME,
                    currentTime + CachingPolicy.ChannelsService.cachingDuration,
                );
            }
        } catch (error) {
            Log.e(this.TAG, 'updateChannelsCache()', error);
            if (!Storage.getInstance().getItem(Storage.Keys.CHANNELS)) throw error;
        }
    }

    private getChannelsFromStore(): Array<Channel> {
        Log.i(this.TAG, 'getChannelsFromStore', 'fetching channels from updated store');
        return Storage.getInstance().getItem(Storage.Keys.CHANNELS);
    }

    // eslint-disable-next-line no-unused-vars
    private getfilteredChannels(channels?: Channel[]): Channel[];

    // eslint-disable-next-line no-unused-vars
    private getfilteredChannels(channels?: IChannel[]): IChannel[];

    private getfilteredChannels(channels?: any): any {
        Log.i(this.TAG, 'filtering channels based on various flags');
        let filteredChannels = channels;
        filteredChannels = DeviceFeatures.shouldFilterJumpChannels()
            ? filteredChannels?.filter((channel) => channel.sourceType !== SourceType.JumpChannel)
            : filteredChannels;

        filteredChannels = DeviceFeatures.shouldFilterPpvChannels()
            ? filteredChannels?.filter((channel) => !channel.isPayPerView)
            : filteredChannels;

        filteredChannels = DeviceFeatures.shouldFilter4KChannels()
            ? filteredChannels?.filter((channel) => channel.videoResolution !== VideoResolution.Uhd)
            : filteredChannels;
        return filteredChannels;
    }

    private updateArrayIndexMapping(): void {
        // TODO: remove this filtering once channel service is updated
        let channels = this.getChannelsFromStore();
        channels = this.getfilteredChannels(channels);
        Storage.getInstance().setItem(Storage.Keys.CHANNELS, channels);
        let subscribedChannelsIndex = 0;
        if (channels) {
            channels.forEach((channel, _index) => {
                ChannelsRepositoryImpl.allChannelsIndexMap[channel.channelNumber!] = _index;
                ChannelsRepositoryImpl.allChannels.push(channel.channelNumber!);
                if (channel.isSubscribed) {
                    ChannelsRepositoryImpl.subscribedChannels.push(channel.channelNumber!);
                    ChannelsRepositoryImpl.subscribedChannelsIndexMap[channel.channelNumber!] =
                        subscribedChannelsIndex;
                    subscribedChannelsIndex += 1;
                }
            });
        }
        const stationPolicyData = Storage.getInstance().getItem(Storage.Keys.STATION_POLICIES);
        if (stationPolicyData) {
            stationPolicyData.forEach((policy: StationPolicy) => {
                ChannelsRepositoryImpl.stationPoliciesMap.set(policy.name, policy);
            });
        }
    }

    private getChannelIndexFromSubscribedChannels(channelNumber: string): number {
        Log.i(
            this.TAG,
            'getChannelIndexFromSubscribedChannels',
            `searching channel: ${channelNumber} in subscribed channels`,
        );
        const currentChannelIndex =
            ChannelsRepositoryImpl.subscribedChannelsIndexMap[channelNumber];
        if (typeof currentChannelIndex === 'number') return currentChannelIndex;
        return -1;
    }

    // eslint-disable-next-line class-methods-use-this
    private getChannelsAndChannelsIndexMap(channelsType: string) {
        let channelsIndexMap;
        let channels;
        switch (channelsType) {
            case ChannelsType.ALL:
                channelsIndexMap = ChannelsRepositoryImpl.allChannelsIndexMap;
                channels = ChannelsRepositoryImpl.allChannels;
                break;
            case ChannelsType.SUBSCRIBED: // subscribed
            default:
                channels = ChannelsRepositoryImpl.subscribedChannels;
                channelsIndexMap = ChannelsRepositoryImpl.subscribedChannelsIndexMap;
                break;
        }
        return { channels, channelsIndexMap };
    }

    private getSubscribedChannel(channelIndex: number, type: SubscribedChannelsType): Channel {
        const allChannels = this.getChannelsFromStore();
        const totalChannels = allChannels.length;
        const incrementer = type === SubscribedChannelsType.NEXT ? 1 : -1;
        const currentChannelIndex = channelIndex % totalChannels;
        let requiredChannelIndex =
            (currentChannelIndex + incrementer + totalChannels) % totalChannels;
        while (requiredChannelIndex !== currentChannelIndex) {
            const requiredChannel = allChannels[requiredChannelIndex];
            requiredChannel.policyInfo = requiredChannel.policyName?.map((policy) => {
                return ChannelsRepositoryImpl.stationPoliciesMap.get(policy);
            });
            if (requiredChannel.isSubscribed) return requiredChannel;
            requiredChannelIndex =
                (requiredChannelIndex + incrementer + totalChannels) % totalChannels;
        }
        throw Error('No subscribed channel found');
    }

    private getCurrentChannelAndIndex(channelNumber): {
        currentChannel: Channel;
        currentChannelIndex: number;
    } {
        const allChannels = this.getChannelsFromStore();
        const currentChannelIndex = ChannelsRepositoryImpl.allChannelsIndexMap[channelNumber];
        const currentChannel = allChannels[currentChannelIndex];
        currentChannel.policyInfo = currentChannel.policyName?.map((policy) => {
            return ChannelsRepositoryImpl.stationPoliciesMap.get(policy);
        });
        return { currentChannel, currentChannelIndex };
    }

    private async getChannels(
        type: ChannelsType,
        offset?: number,
        limit?: number,
    ): Promise<TChannels> {
        try {
            if (limit && limit < 0) throw new Error('invalid value of limit');
            if (offset && offset < 0) throw new Error('invalid value of offset');

            await this.updateChannelsCache();
            const allChannels = this.getChannelsFromStore();
            const channels: Array<string> = ChannelsRepositoryImpl[type];
            const channelCount = channels.length;
            const start = !offset ? 0 : offset;
            const end = start + (limit || channelCount);
            let requiredChannels = channels.slice(start, end);
            /**
             * logic to add channels from starting if the available channels from offset does not
             * fullfil the limit. it will append the remaining channels from the starting,
             * producing cyclic view in guide
             */
            if (end > channelCount) {
                requiredChannels = requiredChannels.concat(channels.slice(0, end % channelCount));
            }

            const requiredChannelsData = requiredChannels.map((channelNumber) => {
                const channelIndex = ChannelsRepositoryImpl.allChannelsIndexMap[channelNumber];
                const requiredChannel = allChannels[channelIndex];
                requiredChannel.policyInfo = requiredChannel.policyName?.map((policy) => {
                    return ChannelsRepositoryImpl.stationPoliciesMap.get(policy);
                });
                return requiredChannel;
            });

            return <TChannels>{
                channels: requiredChannelsData,
                channelCount,
                currentOffset: offset,
            };
        } catch (error) {
            Log.e(this.TAG, 'getChannels()', error);
            throw error;
        }
    }

    /**
     * returns array of subscribed channels object for the given range,
     * if limit and offset is not passed, array of all subscribed channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannels>} object of {channels,channelCount}
     */
    async fetchMyChannels(offset?: number, limit?: number): Promise<TChannels> {
        return this.getChannels(ChannelsType.SUBSCRIBED, offset, limit);
    }

    /**
     * returns array of channels object for the given range,
     * if limit and offset is not passed, array of all channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannels>} object of {channels,channelCount}
     */
    async fetchAllChannels(offset?: number, limit?: number): Promise<TChannels> {
        return this.getChannels(ChannelsType.ALL, offset, limit);
    }

    /**
     * return next channel object from the currnt channel number
     * @param channelNumber {string} channel number
     * @returns {Channel} channel object of next channel
     */
    public async getNextChannel(channelNumber: string, channelsType: string, count: number = 1,): Promise<Channel> {
        try {
            await this.updateChannelsCache();
            const { channels, channelsIndexMap } =
                this.getChannelsAndChannelsIndexMap(channelsType);
            const currentChannelIndex = channelsIndexMap[channelNumber];
            const allChannels = this.getChannelsFromStore();
           const nextChannelIndex =
                (currentChannelIndex + (count % channels.length)) % channels.length;
            const nextChannelNumber = channels[nextChannelIndex];
            const nextChannelIndexInAllChannels =
                ChannelsRepositoryImpl.allChannelsIndexMap[nextChannelNumber];
            const nextRequiredChannel = allChannels[nextChannelIndexInAllChannels];
            nextRequiredChannel.policyInfo = nextRequiredChannel.policyName?.map((policy) => {
                return ChannelsRepositoryImpl.stationPoliciesMap.get(policy);
            });
            return nextRequiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'getNextChannel', error);
            throw error;
        }
    }

    /**
     * return previous channel object from the currnt channel number
     * @param channelNumber {string} channel number
     * @returns {Channel} channel object of previous channel
     */
    public async getPreviousChannel(channelNumber: string, channelsType: string, count: number = 1,): Promise<Channel> {
        try {
            const { channels, channelsIndexMap } =
                this.getChannelsAndChannelsIndexMap(channelsType);
            await this.updateChannelsCache();
            const allChannels = this.getChannelsFromStore();
            const currentChannelIndex = channelsIndexMap[channelNumber];
            const previousChannelIndex =
                (currentChannelIndex - (count % channels.length) + channels.length) %
                channels.length;
            const previousChannelNumber = channels[previousChannelIndex];
            const previousChannelIndexInAllChannels =
                ChannelsRepositoryImpl.allChannelsIndexMap[previousChannelNumber];
            const previousRequiredChannel = allChannels[previousChannelIndexInAllChannels];
            previousRequiredChannel.policyInfo = previousRequiredChannel.policyName?.map(
                (policy) => {
                    return ChannelsRepositoryImpl.stationPoliciesMap.get(policy);
                },
            );
            return previousRequiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'getPreviousChannel', error);
            throw error;
        }
    }

    /**
     * return obect of requested channel bumber
     * @param channelNumber {string} requested channel number
     * @returns {Channel} channel object of requested channel
     */
    async tuneToChannel(channelNumber: string): Promise<Channel> {
        try {
            const currentChannelIndex = ChannelsRepositoryImpl.allChannelsIndexMap[channelNumber];
            if (currentChannelIndex === undefined)
                throw new Error(`cannot find channel with channel number: ${channelNumber}`);
            const channels = this.getChannelsFromStore();
            const requiredChannel = channels[currentChannelIndex];
            requiredChannel.policyInfo = requiredChannel.policyName?.map((policy) => {
                return ChannelsRepositoryImpl.stationPoliciesMap.get(policy);
            });
            return requiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'tuneToChannel', error);
            throw error;
        }
    }

    async fetchNextChannelsFromChannelNumber(
        channelNumber: string,
        channelsType: ChannelsType,
        limit: number,
    ): Promise<TChannels> {
        try {
            await this.updateChannelsCache();
            const channelsIndexMap =
                channelsType === ChannelsType.ALL
                    ? ChannelsRepositoryImpl.allChannelsIndexMap
                    : ChannelsRepositoryImpl.subscribedChannelsIndexMap;
            const currentChannelIndex = channelsIndexMap[channelNumber];
            if (currentChannelIndex)
                return this.getChannels(channelsType, currentChannelIndex, limit);
            const channelNumbers = Object.keys(channelsIndexMap);
            const nextChannelNumberIndex = channelNumbers.findIndex(
                (number) => Number(number) > Number(channelNumber),
            );
            if (nextChannelNumberIndex <= 0)
                return this.getChannels(channelsType, channelNumbers.length - 1, limit);
            return this.getChannels(channelsType, nextChannelNumberIndex - 1, limit);
        } catch (error) {
            Log.e(this.TAG, 'fetchNextChannesl', error);
            throw error;
        }
    }

    updateChannelUrl(channelNumber: string, url: string) {
        const currentChannelIndex = ChannelsRepositoryImpl.allChannelsIndexMap[channelNumber];
        const allChannels = this.getChannelsFromStore();
        allChannels[currentChannelIndex].tuningUri = [url];
        Storage.getInstance().setItem(Storage.Keys.CHANNELS, allChannels);
    }
}
